import { writable, derived, get, type Readable } from "svelte/store";
import type { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import type {
  MoneyV2,
  Maybe,
  SendCreateCheckoutMutation,
  SendCheckoutCustomerAssociateMutation,
  ImageConnection,
  Image,
} from "$lib/gql/gen/codegen";
import {
  CurrencyCode,
  SendCreateCheckoutDoc,
  SendCheckoutCustomerAssociateDoc,
} from "$lib/gql/gen/codegen";
import client from "$lib/gql/apolloClient";
import cfg from "$lib/config/config";
import { pushGtmEcommerceEvent } from "../utils/gtmEvent";
import { goto } from "$app/navigation";
import { regionStore, type RegionStore } from "./region";
import { account } from "./account";
import { checkout } from "./checkout";
import { recommendations } from "./recommendations";
import { voyadoUpdateCart } from "$lib/utils/voyado";
import { linkTo } from "$lib/utils/url";
import { browser } from "$app/environment";

export type BasketStore = ReturnType<typeof basketStore>;

export type CheckoutLineItem = {
  [x: string]: any;
  id: string;
  quantity: number;
  title: string;
  url: string;
  images?: ImageConnection | Image[];
  variant?: Maybe<ProductVariant>;
};

export type ProductVariant = {
  id: string;
  title: string;
  price: MoneyV2;
  sku?: string;
  compareAtPrice?: MoneyV2;
  selectedOptions: { name: string; value: string }[];
  product?: {
    id?: string;
    material?: { value: string };
  };
  quantityAvailable?: number;
};

export type Product = {
  title: string;
  variant?: Maybe<ProductVariant>;
};

export type Totals = {
  subtotalPrice: MoneyV2;
  totalPrice: MoneyV2;
  totalTax: MoneyV2;
};

const getCheckoutInfo = async (
  basket: Readable<CheckoutLineItem[]>,
  regionInfo: RegionStore,
) => {
  const items = get(basket).map(item => ({
    quantity: item.quantity,
    variantId: item.id,
  }));
  try {
    const res = await client.mutate<SendCreateCheckoutMutation>({
      mutation: SendCreateCheckoutDoc,
      variables: {
        items,
      },
    });

    const locale = get(regionInfo).locale;

    if (get(account)?.accessToken) {
      await account.renewToken();
      if (get(account)?.accessToken) {
        try {
          const associateRes =
            await client.mutate<SendCheckoutCustomerAssociateMutation>({
              mutation: SendCheckoutCustomerAssociateDoc,
              variables: {
                checkoutId: res.data?.checkoutCreate?.checkout?.id,
                customerAccessToken: get(account)?.accessToken,
              },
            });

          const checkoutUrl =
            associateRes.data?.checkoutCustomerAssociateV2?.checkout?.webUrl +
            (locale ? `&locale=${locale}` : "");

          return {
            url: checkoutUrl,
            checkoutId: res.data?.checkoutCreate?.checkout?.id,
          };
        } catch (error) {
          console.error(error);
        }
      }
    } else {
      return {
        url:
          res.data?.checkoutCreate?.checkout?.webUrl +
          (locale ? `&locale=${locale}` : ""),
        checkoutId: res.data?.checkoutCreate?.checkout?.id,
      };
    }
  } catch (error) {
    console.error(error);
  }
};

export function basketStore(
  client: ApolloClient<NormalizedCacheObject>,
  regionStore: () => RegionStore,
  namespace: string,
  localStorage?: Storage,
  pushGtmEvents = false,
) {
  let initialState = [];
  if (localStorage) {
    try {
      const basketString = localStorage.getItem(`${namespace}:basket`);
      initialState = basketString ? JSON.parse(basketString) : [];
    } catch {
      initialState = [];
    }
  }
  const basket = writable<CheckoutLineItem[]>(initialState);

  // initialise subscription to update local storage
  if (localStorage) {
    basket.subscribe(items => {
      localStorage.setItem(`${namespace}:basket`, JSON.stringify(items));
    });
  }

  return {
    subscribe: basket.subscribe,
    set: basket.set,
    addToBasket: (
      newItem: ProductVariant,
      title: string,
      images: ImageConnection | Image[],
      url: string,
      regionInfo: RegionStore,
    ) => {
      if (!regionInfo) {
        regionInfo = regionStore();
      }
      basket.update(items => {
        let itemIsInBasket = false;
        items.map(item => {
          if (item.id === newItem.id) {
            itemIsInBasket = true;
            // increment quantity
            return {
              ...item,
              quantity: item.quantity++,
            };
          }
        });
        if (!itemIsInBasket) {
          return [
            // Added to catch any saved baskets which might still have a free item in them
            ...items.filter(item => !item.freeItem),
            {
              id: newItem.id,
              title: title,
              quantity: 1,
              url: url,
              images: images,
              variant: newItem,
            },
          ];
        }
        // Added to catch any saved baskets which might still have a free item in them
        return items.filter(item => !item.freeItem);
      });
      if (pushGtmEvents)
        pushGtmEcommerceEvent("add_to_cart", [
          {
            id: newItem.id,
            title: title,
            quantity: 1,
            url: url,
            images: images,
            variant: newItem,
          },
        ]);
      recommendations.setBasket();

      const items = get(basket);
      if (items.length > 0) {
        const regionInfoData = get(regionInfo);
        const locale = regionInfoData.locale;
        const region = regionInfoData.region;
        if (region !== undefined && locale !== undefined) {
          getCheckoutInfo(basket, regionInfo).then(checkoutInfo => {
            if (checkoutInfo && checkoutInfo.url && checkoutInfo.checkoutId) {
              voyadoUpdateCart(
                items,
                checkoutInfo.url,
                checkoutInfo.checkoutId,
                region,
                locale,
              );
            }
          });
        }
      }
    },
    setQuantity: async (id: string, quantity: number): Promise<void> => {
      const regionInfo = regionStore();
      let prevQuantity: number | undefined;
      let basketItemForAnalytics: CheckoutLineItem | undefined;

      basket.update(items =>
        items
          .reduce<CheckoutLineItem[]>((acc, item) => {
            if (item.id === id) {
              prevQuantity = item.quantity;
              if (quantity === 0) {
                // The item should be removed, do not push to accumulator
                pushGtmEcommerceEvent("remove_from_cart", [item]);
              } else {
                basketItemForAnalytics = { ...item, quantity };
                acc.push(basketItemForAnalytics);
              }
            } else {
              // This item is not the target item, no updates just push it back.
              acc.push(item);
            }
            return acc;
          }, [])
          .filter(item => !item.freeItem),
      );

      if (
        pushGtmEvents &&
        typeof prevQuantity !== "undefined" &&
        basketItemForAnalytics
      ) {
        if (quantity > prevQuantity) {
          pushGtmEcommerceEvent("add_to_cart", [basketItemForAnalytics]);
        } else if (quantity < prevQuantity) {
          const removedQuantityItem = {
            ...basketItemForAnalytics,
            quantity: prevQuantity - quantity,
          };
          pushGtmEcommerceEvent("remove_from_cart", [removedQuantityItem]);
        }
      }

      const items = get(basket);
      if (items.length === 0) {
        recommendations.setBasket();
        return;
      }

      const regionInfoData = get(regionInfo);
      const locale = regionInfoData.locale;
      const region = regionInfoData.region;

      if (region !== undefined && locale !== undefined) {
        try {
          const checkoutInfo = await getCheckoutInfo(basket, regionInfo);
          if (checkoutInfo) {
            const { url, checkoutId } = checkoutInfo;
            if (checkoutId !== undefined) {
              await voyadoUpdateCart(items, url, checkoutId, region, locale);
            } else {
              console.error("Error: checkoutId is undefined");
            }
          }
        } catch (error) {
          console.error("Error updating checkout info:", error);
        }
      }
      recommendations.setBasket();
    },
    quantity: derived(basket, items => {
      return items
        .map(item => item.quantity)
        .reduce((prev, current) => prev + current, 0);
    }),
    totals: derived(basket, items => {
      let subTotal = 0;
      const salesTax = 0.2;
      let tax = 0;
      let totalPrice = 0;
      let currencyCode = CurrencyCode.Gbp;

      if (items.length >= 1) {
        totalPrice = items
          .map(item => item.quantity * parseFloat(item.variant?.price.amount))
          .reduce((a, b) => a + b);
        subTotal = totalPrice / (1 + salesTax);
        tax = totalPrice - subTotal;
        const itemCurrencyCode = items[0].variant?.price.currencyCode;
        if (itemCurrencyCode) {
          currencyCode = itemCurrencyCode;
        }
      }

      return {
        subtotalPrice: {
          amount: subTotal.toFixed(2),
          currencyCode,
        },
        totalTax: {
          amount: tax.toFixed(2),
          currencyCode,
        },
        totalPrice: {
          amount: totalPrice.toFixed(2),
          currencyCode,
        },
      };
    }),
    checkout: async (regionInfo: RegionStore) => {
      const items = get(basket).map(item => ({
        quantity: item.quantity,
        variantId: item.id,
      }));
      try {
        const res = await client.mutate<SendCreateCheckoutMutation>({
          mutation: SendCreateCheckoutDoc,
          variables: {
            items,
          },
        });
        const checkoutId = res.data?.checkoutCreate?.checkout?.id;
        if (checkoutId) {
          checkout.set({ id: checkoutId });
        }
        const locale = get(regionInfo).locale;

        if (get(account)?.accessToken) {
          await account.renewToken();
          if (get(account)?.accessToken) {
            try {
              const associateRes =
                await client.mutate<SendCheckoutCustomerAssociateMutation>({
                  mutation: SendCheckoutCustomerAssociateDoc,
                  variables: {
                    checkoutId: checkoutId,
                    customerAccessToken: get(account)?.accessToken,
                  },
                });

              const checkoutUrl =
                associateRes.data?.checkoutCustomerAssociateV2?.checkout
                  ?.webUrl + (locale ? `&locale=${locale}` : "");

              const email = get(account)?.email || "";
              const accessToken = get(account)?.accessToken || "";

              await fetch(
                `${
                  cfg.BASE_PATH || ""
                }/api/multipass.json?email=${encodeURIComponent(
                  email,
                )}&checkoutUrl=${encodeURI(checkoutUrl)}`,
                {
                  method: "GET",
                  headers: {
                    "X-Shopify-Storefront-Access-Token": accessToken,
                  },
                },
              )
                .then(response => {
                  if (response.status === 401) {
                    account.set(null);
                    goto(linkTo("account/login"));
                  } else {
                    return response.json().then(data => {
                      goto(data.url);
                    });
                  }
                })
                .catch(() => {
                  goto(
                    res.data?.checkoutCreate?.checkout?.webUrl +
                      (locale ? `&locale=${locale}` : ""),
                  );
                });
            } catch (error) {
              console.error(error);
            }
          }
        } else {
          goto(
            res.data?.checkoutCreate?.checkout?.webUrl +
              (locale ? `&locale=${locale}` : ""),
          );
        }
      } catch (error) {
        console.error(error);
      }
    },
    clearBasket: () => {
      basket.set([]);
    },
  };
}
export const basket = basketStore(
  client,
  regionStore,
  cfg.SHOPIFY_STORE + "-v3",
  browser && navigator.cookieEnabled ? localStorage : null,
  true,
);
